import { useContext, useEffect, HTMLAttributes } from "react";
import { useIntl } from "react-intl";
import SummaryCard from "../summary-card";
import { Link } from "react-router-dom";
import { ButtonGroup } from "react-bootstrap";
import FieldList from "../../field-list";
import { ModalKeys } from "../../pages/invitations";
import { sortToLatestByCreatedOrUpdated } from "../../../util/objectUtil";
import InvitationStateBadge from "../../badges/invitation-state-badge";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import TooltipTrigger from "../../tooltip-trigger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OrganizationGroupInvitationValues } from "../../../localization/organization-group-invitation";
import { TotalLabel } from "../summary-card/summary-card-view";
import {OrganizationGroupInvitationWithExpiredStatus} from "../../../utils/invitation";
interface InvitationsCardDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {}
export interface InvitationsCardProps extends InvitationsCardDOMProps {
  isReady: boolean;
  invitations?: OrganizationGroupInvitationWithExpiredStatus[] | null;
  onLoadInvitations?: () => {};
  pendingCount?: number;
  unsentCount?: number;
  updatedCount?: number;
  revokedCount?: number;
  acceptedCount?: number;
  declinedCount?: number;
  expiredCount?: number;

  moreAction: () => void;
}

function InvitationsCard(props: InvitationsCardProps) {
  const {
    invitations,
    onLoadInvitations,
    pendingCount,
    unsentCount,
    updatedCount,
    revokedCount,
    acceptedCount,
    declinedCount,
    expiredCount,
    moreAction,
    isReady,
  } = props;
  const intl = useIntl();
  const { conf } = useContext(UIConfiguration);
  const invitationsConf =
    conf.functionality && conf.functionality.invitations
      ? conf.functionality.invitations
      : ({} as any);
  const headerButton = {
    label: intl.formatMessage({
      defaultMessage: "View",
      description: "label for the view button",
    }),
    action: moreAction,
  };
  if (!onLoadInvitations && invitations === undefined) {
    throw new Error(
      "GroupsCard: Required props missing. Either groups or onLoadGroups must be defined"
    );
  }
  const isInvitationsUndefined = invitations === undefined;
  useEffect(() => {
    if (isInvitationsUndefined && onLoadInvitations) {
      onLoadInvitations();
    }
  }, [isInvitationsUndefined, onLoadInvitations]);
  const fields: any = {
    total: {
      label: intl.formatMessage(TotalLabel),
      value: invitations ? invitations.length : 0,
    },
  };
  if (unsentCount) {
    fields.unsent = {
      label: intl.formatMessage(
        OrganizationGroupInvitationValues.invitationState.created
      ),
      value: unsentCount,
    };
  }
  if (invitationsConf.revoke && revokedCount) {
    fields.revoked = {
      label: intl.formatMessage(
        OrganizationGroupInvitationValues.invitationState.revoked
      ),
      value: revokedCount,
    };
  }
  if (declinedCount) {
    fields.declined = {
      label: intl.formatMessage(
        OrganizationGroupInvitationValues.invitationState.declined
      ),
      value: declinedCount,
    };
  }
  if (acceptedCount) {
    fields.accepted = {
      label: intl.formatMessage(
        OrganizationGroupInvitationValues.invitationState.accepted
      ),
      value: acceptedCount,
    };
  }
  if (updatedCount) {
    fields.updated = {
      label: intl.formatMessage(
        OrganizationGroupInvitationValues.invitationState.updated
      ),
      value: updatedCount,
    };
  }
  if (pendingCount) {
    fields.pending = {
      label: intl.formatMessage(
          OrganizationGroupInvitationValues.invitationState.deliveryRequested
      ),
      value: pendingCount,
    };
  }
  if (expiredCount) {
    fields.expired = {
      label: intl.formatMessage(
          OrganizationGroupInvitationValues.invitationState.expired
      ),
      value: expiredCount,
    };
  }
  return (
    <SummaryCard
      onReloadData={() => {
        if (onLoadInvitations) {
          onLoadInvitations();
        }
      }}
      isReady={isReady}
      data-test-invitations-card
      title={intl.formatMessage({
        defaultMessage: "Invitations",
        description: "heading for the card",
      })}
      headerButton={headerButton}
      items={
        invitations && invitations.length
          ? invitations
              // then unsent invitations
              .filter((i) => i.invitationState === "created")
              .sort(sortToLatestByCreatedOrUpdated)
              .concat(
                  invitations
                      // then revoked invitations
                      .filter((i) => i.invitationState === "revoked")
                      .sort(sortToLatestByCreatedOrUpdated)
              )
              .concat(
                  invitations
                      // then expired invitations
                      .filter((i) => i.invitationState === "expired")
                      .sort(sortToLatestByCreatedOrUpdated)
              )
              // then closed invitations
              .concat(
                invitations
                  .filter((i) => i.invitationState === "declined")
                  .sort(sortToLatestByCreatedOrUpdated)
              )
              .concat(
                invitations
                  .filter((i) => i.invitationState === "accepted")
                  .sort(sortToLatestByCreatedOrUpdated)
              )
              // and finally the rest
              .concat(
                invitations
                  .filter(
                    (i) =>
                      i.invitationState !== "created" &&
                      i.invitationState !== "expired" &&
                      i.invitationState !== "accepted" &&
                      i.invitationState !== "declined" &&
                      i.invitationState !== "revoked"
                  )
                  .sort(sortToLatestByCreatedOrUpdated)
              )
              .map((i) => {
                const Wrapper = invitationsConf.show ? Link : "span";
                const wrapperProps: any = {
                  className:
                    "flex-shrink-1 d-flex justify-content-between align-items-center top-most mr-2",
                };
                if (invitationsConf.show) {
                  wrapperProps.to =
                    (("/invitations/" + i.id) as string) + "/" + ModalKeys.show;
                }
                return (
                  <>
                    <Wrapper {...wrapperProps}>
                      <TooltipTrigger
                        tipKey={"invitation_" + i.id}
                        tip={i.email}
                        placement={"top"}
                      >
                        <span className={"flex-shrink-1 mr-2 truncate"}>
                          {i.email}
                        </span>
                      </TooltipTrigger>
                      <InvitationStateBadge
                          state={i.invitationState}
                          validUntil={i.validUntil}
                          validFrom={i.validFrom}
                      />
                    </Wrapper>
                    {(invitationsConf.resend ||
                      invitationsConf.revoke ||
                      invitationsConf.remove) && (
                      <ButtonGroup size={"sm"} className={"ml-2 float-right"}>
                        {i.invitationState !== "declined" &&
                          i.invitationState !== "accepted" &&
                          i.invitationState !== "revoked" &&
                          i.invitationState !== "expired" && (
                            <>
                              {invitationsConf.resend && (
                                <Link
                                  className={"btn btn-theme-to-secondary"}
                                  to={
                                    (("/invitations/" + i.id) as string) +
                                    "/" +
                                    ModalKeys.resend
                                  }
                                >
                                  {intl.formatMessage({
                                    defaultMessage: "Resend",
                                    description:
                                      "Label for the resend button. Can be translated as 'send' if it helps keeping the translation short, no need to emphasize that something is sent again.",
                                  })}
                                </Link>
                              )}
                              {invitationsConf.revoke &&
                                i.invitationState !== "created" && (
                                  <Link
                                    className={"btn btn-theme-to-secondary"}
                                    to={
                                      (("/invitations/" + i.id) as string) +
                                      "/" +
                                      ModalKeys.revoke
                                    }
                                  >
                                    {intl.formatMessage({
                                      defaultMessage: "Revoke",
                                      description:
                                        "label for the revoke button",
                                    })}
                                  </Link>
                                )}
                            </>
                          )}
                        {invitationsConf.remove &&
                          (i.invitationState === "created" ||
                            i.invitationState === "expired" ||
                            i.invitationState === "declined" ||
                            i.invitationState === "accepted" ||
                            i.invitationState === "revoked") && (
                            <Link
                              className={"btn btn-theme-to-secondary"}
                              to={
                                (("/invitations/" + i.id) as string) +
                                "/" +
                                ModalKeys.remove
                              }
                            >
                              {intl.formatMessage({
                                defaultMessage: "Remove",
                                description: "label for the remove button",
                              })}
                            </Link>
                          )}
                      </ButtonGroup>
                    )}
                  </>
                );
              })
          : undefined
      }
      footer={<FieldList inline={true} fields={fields} />}
    >
      {invitations && invitations.length === 0 && (
        <div data-test-no-items className={"text-muted text-center py-4"}>
          <div className={"icon pb-1 mb-3"}>
            <span className="fa-layers fa-fw fa-2x">
              <FontAwesomeIcon
                icon="circle"
                mask={"database"}
                className={"opacity-50"}
                transform="shrink-0 right-5 down-5"
              />
              <FontAwesomeIcon icon="ban" transform="shrink-4 right-5 down-5" />
            </span>
          </div>
          <p className={"mb-0"}>
            {intl.formatMessage({
              defaultMessage: "No invitations to show.",
              description: "display value for no invitations",
            })}
          </p>
        </div>
      )}
    </SummaryCard>
  );
}
export default InvitationsCard;
